import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import ProfileIcon from "../../../assets/icons/profile.svg";
import ColorProfile from "../../../assets/images/color_profile.svg";
import { FiExternalLink, FiEdit2, FiCheck, FiX, FiCamera } from "react-icons/fi";
import { getAppPrefix } from "../utility/embedHelper";
import { fetchMyData } from "../../../redux/slices/myDataSlice";
import {
  postRequestAuthenticated,
  postRequestUnAuthenticated,
  postRequestAuthenticatedWithFile,
} from "../../../services/rest";
import useModal from "../../hooks/ModalHook";

const Avatar = ({ logo, color_logo, size = "h-20 w-20", inner = "w-7 h-7" }) => {
  if (logo)
    return <img src={logo} alt="" className={`${size} rounded-full object-cover`} />;
  if (color_logo)
    return (
      <div
        className={`${size} flex items-center justify-center rounded-full`}
        style={{ backgroundColor: color_logo }}
      >
        <img src={ColorProfile} alt="" className={inner} />
      </div>
    );
  return <img src={ProfileIcon} alt="" className={`${size} rounded-full object-cover`} />;
};

const BackBar = ({ title, onBack }) => (
  <div className="flex items-center gap-3 border-b border-theme-chatDivider px-4 py-3">
    <button
      onClick={onBack}
      aria-label="Back"
      className="flex h-8 w-8 items-center justify-center rounded-full text-xl text-theme-secondaryText hover:bg-theme-tertiaryBackground"
    >
      ‹
    </button>
    <p className="font-medium text-theme-secondaryText">{title}</p>
  </div>
);

const EmbedProfilePage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const isBrand = searchParams.get("view") === "brand";
  const { handleOpenModal } = useModal();

  const myData = useSelector((s) => s.myData);
  const branding = useSelector((s) => s.embedHome?.branding) || {};
  const features = useSelector((s) => s.embedHome?.features) || {};
  const username = useSelector((s) => s.embedHome?.username);
  const isLoggedIn = useSelector((s) => s.auth.isLoggedIn);

  // Whose profile is in the URL? If it's not me, show THAT member's profile.
  const { username: routeUsername } = useParams();
  const isOtherProfile =
    !isBrand && routeUsername && myData?.username && routeUsername !== myData.username;
  const [otherUser, setOtherUser] = useState(null);
  const [loadingOther, setLoadingOther] = useState(false);

  useEffect(() => {
    if (!isOtherProfile) return;
    setLoadingOther(true);
    postRequestUnAuthenticated("/fetch/user/details", { username: routeUsername })
      .then((res) => {
        if (res?.success) setOtherUser(res.user || null);
      })
      .finally(() => setLoadingOther(false));
  }, [isOtherProfile, routeUsername]);

  const [joined, setJoined] = useState([]);
  const [loadingJoined, setLoadingJoined] = useState(false);

  // Profile picture upload (member's own avatar).
  const [picUploading, setPicUploading] = useState(false);
  const picInputRef = useRef(null);
  const uploadProfilePic = (file) => {
    if (!file) return;
    setPicUploading(true);
    const fd = new FormData();
    fd.append("image", file);
    postRequestAuthenticatedWithFile("/update/profile/picture", fd)
      .then((res) => {
        if (res?.success) dispatch(fetchMyData()); // refresh myData.logo
      })
      .finally(() => setPicUploading(false));
  };

  // Username editing (globally unique).
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState("");
  const [nameStatus, setNameStatus] = useState(null); // null | "checking" | "ok" | "taken" | "invalid"
  const [savingName, setSavingName] = useState(false);
  const [nameError, setNameError] = useState("");

  const usernameValid = (u) => /^[a-zA-Z0-9_.]{3,30}$/.test(u);

  const startEditName = () => {
    setNameInput(myData?.username || "");
    setNameStatus(null);
    setNameError("");
    setEditingName(true);
  };

  // Debounced availability check while typing.
  useEffect(() => {
    if (!editingName) return;
    const u = nameInput.trim();
    if (u === (myData?.username || "")) {
      setNameStatus(null);
      return;
    }
    if (!usernameValid(u)) {
      setNameStatus("invalid");
      return;
    }
    setNameStatus("checking");
    const t = setTimeout(() => {
      postRequestAuthenticated("/check/username/profile", { username: u })
        .then((res) => setNameStatus(res?.success ? "ok" : "taken"))
        .catch(() => setNameStatus(null));
    }, 450);
    return () => clearTimeout(t);
  }, [nameInput, editingName, myData?.username]);

  const saveUsername = () => {
    const u = nameInput.trim();
    if (!usernameValid(u) || nameStatus === "taken") return;
    setSavingName(true);
    setNameError("");
    postRequestAuthenticated("/update/username", { username: u })
      .then((res) => {
        if (res?.success) {
          setEditingName(false);
          dispatch(fetchMyData());
        } else {
          setNameError(res?.message || "Could not update username.");
        }
      })
      .finally(() => setSavingName(false));
  };

  useEffect(() => {
    if (!isBrand && !myData?._id) dispatch(fetchMyData());
  }, [isBrand, myData?._id, dispatch]);

  // Reliable joined-channels source: the authenticated /fetch/my/channels (the
  // unauthenticated channel list's members array isn't user-specific).
  useEffect(() => {
    if (isBrand || !isLoggedIn) return;
    setLoadingJoined(true);
    postRequestAuthenticated("/fetch/my/channels", {})
      .then((res) => {
        if (res?.success)
          setJoined(
            (res.channels || []).filter((c) => c.isMember || c.isOwner)
          );
      })
      .finally(() => setLoadingJoined(false));
  }, [isBrand, isLoggedIn]);

  const goChannel = (c) => navigate(`${getAppPrefix()}/user/${username}/channel/${c._id}`);
  const brandName = branding.displayName || "Community";

  // ---- Brand profile (the business's mini-site) ----
  if (isBrand) {
    return (
      <div className="flex h-full w-full flex-col overflow-y-auto bg-theme-secondaryBackground">
        <BackBar title="About" onBack={() => navigate(-1)} />
        <div className="overflow-hidden">
          {branding.banner ? (
            <img src={branding.banner} alt="" className="h-32 w-full object-cover" />
          ) : (
            <div
              className="h-32 w-full"
              style={{
                background:
                  "linear-gradient(120deg, rgba(242,97,27,.45), rgba(124,58,237,.4))",
              }}
            />
          )}
          <div className="mx-auto w-full max-w-2xl px-5 pb-6">
            <div className="-mt-8 flex items-end gap-3">
              {branding.logo ? (
                <img
                  src={branding.logo}
                  alt={brandName}
                  className="h-16 w-16 flex-shrink-0 rounded-2xl object-cover ring-4 ring-theme-secondaryBackground"
                />
              ) : (
                <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-2xl bg-theme-tertiaryBackground text-2xl font-bold text-theme-secondaryText ring-4 ring-theme-secondaryBackground">
                  {brandName.charAt(0).toUpperCase()}
                </div>
              )}
              <div className="min-w-0 pb-1">
                <p className="truncate text-xl font-semibold text-theme-secondaryText">
                  {brandName}
                </p>
                {branding.tagline && (
                  <p className="truncate text-sm text-theme-placeholder">
                    {branding.tagline}
                  </p>
                )}
              </div>
            </div>

            {branding.description && (
              <p
                className="mt-4 text-sm leading-relaxed text-theme-primaryText"
                style={{ whiteSpace: "pre-line" }}
              >
                {branding.description}
              </p>
            )}

            {branding.links?.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {branding.links.map((l, i) => (
                  <a
                    key={i}
                    href={l.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-full border border-theme-chatDivider px-3 py-1.5 text-xs font-medium text-theme-secondaryText hover:bg-theme-tertiaryBackground"
                  >
                    {l.label}
                    <FiExternalLink size={12} />
                  </a>
                ))}
              </div>
            )}

            {branding.sections?.length > 0 && (
              <div className="mt-5 flex flex-col gap-4">
                {branding.sections.map((s, i) => (
                  <div key={i}>
                    {s.title && (
                      <h3 className="mb-1 text-sm font-semibold text-theme-secondaryText">
                        {s.title}
                      </h3>
                    )}
                    {s.body && (
                      <p
                        className="text-sm leading-relaxed text-theme-primaryText"
                        style={{ whiteSpace: "pre-line" }}
                      >
                        {s.body}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}

            {!branding.description &&
              !branding.links?.length &&
              !branding.sections?.length && (
                <p className="mt-4 text-sm text-theme-placeholder">
                  Welcome to the {brandName} community.
                </p>
              )}
          </div>
        </div>
      </div>
    );
  }

  // ---- Another member's profile (clicked their username) ----
  if (isOtherProfile) {
    const openDM = () =>
      navigate(`${getAppPrefix()}/user/${username}/messages/list/${routeUsername}`);
    return (
      <div className="flex h-full w-full flex-col overflow-y-auto bg-theme-secondaryBackground">
        <BackBar title="Profile" onBack={() => navigate(-1)} />
        <div className="mx-auto flex w-full max-w-2xl flex-col gap-6 px-5 py-6">
          {loadingOther && !otherUser ? (
            <p className="text-sm text-theme-placeholder">Loading…</p>
          ) : otherUser ? (
            <>
              <div className="flex items-center gap-4">
                <Avatar logo={otherUser.logo} color_logo={otherUser.color_logo} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-lg font-semibold text-theme-secondaryText">
                    {otherUser.name || otherUser.username}
                  </p>
                  {otherUser.username && (
                    <p className="truncate text-sm text-theme-placeholder">
                      @{otherUser.username}
                    </p>
                  )}
                </div>
              </div>
              {features.dms !== false && routeUsername !== username && (
                <button
                  onClick={openDM}
                  className="self-start rounded-lg bg-theme-secondaryText px-4 py-2 text-sm font-medium text-theme-primaryBackground"
                >
                  Message
                </button>
              )}
            </>
          ) : (
            <p className="text-sm text-theme-placeholder">User not found.</p>
          )}
        </div>
      </div>
    );
  }

  // ---- User profile (mine) ----
  return (
    <div className="flex h-full w-full flex-col overflow-y-auto bg-theme-secondaryBackground">
      <BackBar title="Profile" onBack={() => navigate(-1)} />
      <div className="mx-auto flex w-full max-w-2xl flex-col gap-6 px-5 py-6">
        {myData?._id ? (
          <div className="flex items-start gap-4">
            <div className="relative flex-shrink-0">
              <Avatar logo={myData.logo} color_logo={myData.color_logo} />
              <button
                type="button"
                onClick={() => picInputRef.current?.click()}
                disabled={picUploading}
                aria-label="Change profile picture"
                className="absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center rounded-full bg-theme-secondaryText text-theme-primaryBackground ring-2 ring-theme-secondaryBackground disabled:opacity-50"
              >
                <FiCamera size={13} />
              </button>
              <input
                ref={picInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  uploadProfilePic(e.target.files?.[0]);
                  e.target.value = "";
                }}
              />
            </div>
            <div className="min-w-0 flex-1">
              {picUploading && (
                <p className="text-xs text-theme-placeholder">Uploading photo…</p>
              )}
              <p className="truncate text-lg font-semibold text-theme-secondaryText">
                {myData.name || myData.username}
              </p>
              {myData.email && (
                <p className="truncate text-sm text-theme-placeholder">
                  {myData.email}
                </p>
              )}
              {!editingName ? (
                <button
                  onClick={startEditName}
                  className="group mt-0.5 inline-flex items-center gap-1.5 text-xs text-theme-placeholder hover:text-theme-secondaryText"
                >
                  <span className="truncate">@{myData.username}</span>
                  <FiEdit2
                    size={11}
                    className="opacity-60 group-hover:opacity-100"
                  />
                </button>
              ) : (
                <div className="mt-1.5">
                  <div className="flex items-center gap-1.5">
                    <div className="flex flex-1 items-center rounded-lg border border-theme-chatDivider bg-theme-tertiaryBackground px-2.5 py-1.5">
                      <span className="text-sm text-theme-placeholder">@</span>
                      <input
                        autoFocus
                        value={nameInput}
                        onChange={(e) =>
                          setNameInput(e.target.value.replace(/\s/g, ""))
                        }
                        onKeyDown={(e) => e.key === "Enter" && saveUsername()}
                        maxLength={30}
                        className="w-full bg-transparent text-sm text-theme-secondaryText outline-none"
                      />
                    </div>
                    <button
                      onClick={saveUsername}
                      disabled={
                        savingName ||
                        nameStatus === "checking" ||
                        nameStatus === "taken" ||
                        nameStatus === "invalid" ||
                        !nameInput.trim()
                      }
                      className="flex h-8 w-8 items-center justify-center rounded-lg bg-theme-secondaryText text-theme-primaryBackground disabled:opacity-40"
                      aria-label="Save username"
                    >
                      <FiCheck size={15} />
                    </button>
                    <button
                      onClick={() => setEditingName(false)}
                      className="flex h-8 w-8 items-center justify-center rounded-lg border border-theme-chatDivider text-theme-secondaryText"
                      aria-label="Cancel"
                    >
                      <FiX size={15} />
                    </button>
                  </div>
                  <p className="mt-1 text-[11px]">
                    {savingName ? (
                      <span className="text-theme-placeholder">Saving…</span>
                    ) : nameError ? (
                      <span className="text-theme-error">{nameError}</span>
                    ) : nameStatus === "checking" ? (
                      <span className="text-theme-placeholder">
                        Checking availability…
                      </span>
                    ) : nameStatus === "invalid" ? (
                      <span className="text-theme-error">
                        3-30 chars: letters, numbers, "." or "_".
                      </span>
                    ) : nameStatus === "taken" ? (
                      <span className="text-theme-error">
                        That username is taken.
                      </span>
                    ) : nameStatus === "ok" ? (
                      <span className="text-green-500">Available</span>
                    ) : (
                      <span className="text-theme-placeholder">
                        Your unique @handle.
                      </span>
                    )}
                  </p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="rounded-xl border border-theme-chatDivider p-4 text-center">
            <p className="mb-3 text-sm text-theme-placeholder">
              You're browsing as a guest.
            </p>
            <button
              onClick={() => navigate(`${getAppPrefix()}/get-started`)}
              className="rounded-lg bg-theme-secondaryText px-4 py-2 text-sm font-medium text-theme-primaryBackground"
            >
              Sign in
            </button>
          </div>
        )}

        {myData?._id && (
          <div>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-theme-placeholder">
              Your channels
            </p>
            {loadingJoined ? (
              <p className="text-sm text-theme-placeholder">Loading…</p>
            ) : joined.length > 0 ? (
              <div className="flex flex-col gap-1.5">
                {joined.map((c) => (
                  <button
                    key={c._id}
                    onClick={() => goChannel(c)}
                    className="flex items-center gap-2 rounded-lg border border-theme-chatDivider px-3 py-2.5 text-left text-sm text-theme-secondaryText hover:bg-theme-tertiaryBackground"
                  >
                    <span className="text-theme-placeholder">#</span>
                    <span className="truncate">{c.name}</span>
                  </button>
                ))}
              </div>
            ) : (
              <p className="rounded-lg border border-theme-chatDivider px-3 py-3 text-xs text-theme-placeholder">
                You haven't joined any channels yet.
              </p>
            )}
          </div>
        )}

        {isLoggedIn && (
          <button
            onClick={() => handleOpenModal("modalEmbedLogoutOpen")}
            className="self-start text-sm font-medium text-theme-error"
          >
            Log out
          </button>
        )}
      </div>
    </div>
  );
};

export default EmbedProfilePage;
